import type { Endpoint } from '../../../../features/catalog/model';
import { exampleObject } from '../../../../lib/endpointFields';
import { outlineSchema, type OutlineNode } from '../../../../lib/schemaOutline';

/** The most fields the returned card tints. */
const MAX_CALLOUTS = 4;

type Obj = Record<string, unknown>;

const isObject = (value: unknown): value is Obj =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** The sample's first item: the first member that is a list of objects under
 *  the `{ data }` envelope, else the payload itself. `listKey` names the list
 *  so the snippet can show the item inside it. */
export interface SampleItem {
  listKey: string | null;
  item: Obj;
}

export const sampleItem = (endpoint: Endpoint): SampleItem | null => {
  const root = exampleObject(endpoint.sampleResponse);
  if (!root) return null;
  const payload: unknown = 'data' in root ? root.data : root;
  if (Array.isArray(payload)) return isObject(payload[0]) ? { listKey: null, item: payload[0] } : null;
  if (!isObject(payload)) return null;
  for (const [key, member] of Object.entries(payload)) {
    if (Array.isArray(member) && isObject(member[0])) return { listKey: key, item: member[0] };
  }
  return { listKey: null, item: payload };
};

/** The output schema's direct fields of the item `sampleItem` picks, found the
 *  same way: under `data` when the schema has it, then the list member under
 *  `listKey`, else the payload object. Empty when the schema has no such node. */
export const itemSchemaFields = (outputSchema: unknown, listKey: string | null): OutlineNode[] => {
  const root = outlineSchema(outputSchema);
  const data = root.find((node) => node.name === 'data');
  const payload = data ? (data.children ?? []) : root;
  if (!listKey) return payload;
  return payload.find((node) => node.name === listKey)?.children ?? [];
};

/** `highlightFields` paths when discovery attached them to this endpoint.
 *  Read structurally: the field is optional on the wire and absent on most. */
const highlightPaths = (endpoint: Endpoint): string[] => {
  const raw: unknown = (endpoint as unknown as Obj).highlightFields;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((h) => (isObject(h) && typeof h.path === 'string' ? [h.path] : []));
};

/** A highlight path relative to one item: `comments[].author.username` ->
 *  `author.username`. Null when the path is outside the item (`nextCursor`). */
const itemPath = (path: string, listKey: string | null): string | null => {
  const at = path.lastIndexOf('[].');
  if (at >= 0) return listKey && path.startsWith(`${listKey}[]`) ? path.slice(at + 3) : null;
  return listKey ? null : path;
};

const valueAt = (item: Obj, path: string): unknown =>
  path.split('.').reduce<unknown>((v, key) => (isObject(v) ? v[key] : undefined), item);

/** The fields the hero tints before it has measured the drawn page: the
 *  endpoint's highlighted fields that the sample item actually carries. */
export const highlightedFields = (endpoint: Endpoint, sample: SampleItem): string[] =>
  [...new Set(highlightPaths(endpoint).map((p) => itemPath(p, sample.listKey)))]
    .filter((p): p is string => p !== null && valueAt(sample.item, p) !== undefined)
    .slice(0, MAX_CALLOUTS);
